import { AutodeskViewer } from "../components/autodeskViewer";
import { ILabel, ISpaceOnMap, IVector } from "../interfaces";
import { CameraUtils } from "./cameraUtils";

const defaultLabel: ILabel = {
	text: "",
	font: "poppins",
	size: 16,
	height: 0,
	hexColor: "#000000"
};

export class LabelManager {
	private readonly labelsScene: string = "be-labels";
	private nonCollidingLabels: LabelMesh[] = [];
	public labelsCleaned: boolean = false;
	private initialCameraPosition?: IVector = undefined;

	public constructor(private autodeskViewer: AutodeskViewer, private showLabelsAtZoomLevel?: number, private hideOverlappingLabels?: boolean) { }

	// Sets initial camera position for zoomfactor
	public set cameraPosition(position: THREE.Vector3) {
		if (this.initialCameraPosition == null) {
			this.initialCameraPosition = {
				x: position.x,
				y: position.y,
				z: position.z,
			};
		}
	}

	private get viewer(): Autodesk.Viewing.GuiViewer3D {
		return this.autodeskViewer.viewer;
	}

	public applyLabels(spaces: ISpaceOnMap[], forceLabels: boolean, showLabelsOnZoom: boolean) {
		if (!this.viewer.model) {
			return;
		}

		let spacesWithLabels = spaces.filter((space) => space.style?.placeLabel);

		if (!spacesWithLabels.length) {
			return;
		}

		this.labelsCleaned = false;

		if (!this.viewer.overlays.hasScene(this.labelsScene)) {
			this.viewer.overlays.addScene(this.labelsScene);
		}

		const cameraPosition = this.viewer.getCamera().position;
		const canvas = this.viewer.impl.getCanvasBoundingClientRect();
		const currentZoom = CameraUtils.getCurrentZoomPercentage(cameraPosition, this.initialCameraPosition);
		const zoomLevelTreshold = this.showLabelsAtZoomLevel ?? 250;
		const triggerOnZoomLevel = !showLabelsOnZoom ? false : currentZoom > zoomLevelTreshold; // Don't show labels if the user doesn't want labels when zoomed in

		if (!(forceLabels || triggerOnZoomLevel)) { // Don't draw labels untill zoomed in at set percentage or the button force labels is activated
			return;
		}

		spacesWithLabels = spacesWithLabels.filter((sp) => {
			const spClient = this.viewer.impl.worldToClient(new THREE.Vector3(sp.position.x, sp.position.y, 0));
			return spClient.x > 0 && spClient.x < canvas.width && spClient.y > 0 && spClient.y < canvas.height;
		});

		const rotation = CameraUtils.getRotation(this.viewer);

		spacesWithLabels.forEach((space: ISpaceOnMap) => {
			const style = space.style;

			if (style != null && style.placeStyle && style.placeLabel) {
				const placeLabel = { ...defaultLabel, ...style.placeLabel };

				if (cameraPosition) {
					placeLabel.size = this.getLabelSize(cameraPosition, placeLabel.size!);
				}

				const newLabel = this.createLabel(placeLabel, space, rotation?.quaternion);

				if (this.hideOverlappingLabels) {

					const collidesWithOtherLabel = this.nonCollidingLabels.some((existingLabel) =>
						existingLabel.boundingBox.isIntersectionBox(newLabel.boundingBox)
					);

					if (collidesWithOtherLabel) { return; }
				}

				this.viewer.overlays.addMesh([newLabel.mesh], this.labelsScene);
				this.nonCollidingLabels.push(newLabel);
				newLabel.mesh.geometry.dispose();
				newLabel.mesh.material.dispose();
			}
		});
	}

	public cleanLabels(): void {
		this.labelsCleaned = true;
		this.nonCollidingLabels = [];
		this.viewer.overlays.clearScene(this.labelsScene);
	}

	private getLabelSize(cameraPosition: THREE.Vector3, configuredSize: number): number | undefined {

		const sizeFactor = configuredSize / 16; // 16 'pixels' will result in a factor of 1, 32 'pixels' will be a factor 2

		const zoomCorrectionFactor = 50; // Empirically determinded factor

		const labelSize = cameraPosition.z / zoomCorrectionFactor * sizeFactor;

		return labelSize;
	}

	private createLabel(label: ILabel, space: ISpaceOnMap, rotation?: THREE.Quaternion): LabelMesh {

		const imageSize = space.style?.placeMarker?.imageSize ?? 0;
		const spacePosition = space.position;

		const labelTextGeom = new THREE["TextGeometry"](label.text, {
			font: label.font,
			size: label.size,
			height: 0,
			curveSegments: 2,
		});

		labelTextGeom.computeBoundingBox();
		const mesh = new THREE.Mesh(labelTextGeom, new THREE.MeshBasicMaterial({ color: label.hexColor }));

		const offsetRadius = label.size! * (16 / (space.style?.placeLabel?.size ?? 16)) * (imageSize / 22); // Offset the label from center of marker with magic calculation

		// const labelSize = new THREE.Box3().setFromObject(mesh).getSize(); // For future improvements

		let degrees = 0;

		if (rotation) { // Calculate quaternion back to degrees
			const myRotation = mesh.rotation.setFromQuaternion(rotation);
			const radians = myRotation.z > 0 ? myRotation.z : 2 * Math.PI + myRotation.z;

			degrees = THREE.Math.radToDeg(radians);

			mesh.rotation.setFromQuaternion(rotation);
		}

		// Calculate label offset x/y from degrees
		const labelX = offsetRadius * Math.cos(Math.PI * 2 * degrees / 360);
		const labelY = offsetRadius * Math.sin(Math.PI * 2 * degrees / 360);

		mesh.position.x = spacePosition.x + labelX;
		mesh.position.y = spacePosition.y + labelY;

		return new LabelMesh(mesh, label.text);
	}
}

class LabelMesh {
	public boundingBox: THREE.Box3;
	public mesh: THREE.Mesh;
	public text: string;

	public constructor(public labelMesh: THREE.Mesh, public textParam: string) {
		this.mesh = labelMesh;
		this.text = textParam;
		this.boundingBox = new THREE.Box3().setFromObject(labelMesh);
	}
}